/* Nividas PDP info column behavior — May 2026.
   - Size radio change: keep the eyebrow value, the size hint blurb, and the
     hidden product <select> in sync. The theme's existing variant change
     code listens on <select> changes, so by writing the new variant.id
     into the select and dispatching a change event we get the price/stock
     cascade for free.
   - "See sizing" button: open the Sizing accordion, scroll the size block
     into view (top-aligned with a small offset). */

(function () {
  'use strict';

  function findSection(el) {
    return el && el.closest('[data-section-type="nv-pdp"]');
  }

  function variantsFor(section) {
    const ta = section.querySelector('[data-variant-json]');
    if (!ta) return [];
    try { return JSON.parse(ta.textContent.trim()); } catch (_) { return []; }
  }

  function readSelectedOptions(section) {
    const inputs = section.querySelectorAll('[data-variant-input]');
    const selected = [];
    inputs.forEach((inp) => {
      if (inp.checked || (inp.tagName === 'SELECT')) {
        const idx = parseInt((inp.dataset.index || '').replace('option', ''), 10);
        if (!Number.isNaN(idx)) selected[idx - 1] = inp.value;
      }
    });
    return selected;
  }

  function findVariant(variants, options) {
    return variants.find((v) => {
      return options.every((opt, i) => {
        if (opt == null) return true;
        const k = 'option' + (i + 1);
        return v[k] === opt;
      });
    });
  }

  // Mirror of snippets/nv-money.liquid — produces "1299 SEK" or "1299.50 SEK".
  // Self-contained so we don't depend on Shopify.formatMoney being loaded.
  function fmt(section, cents) {
    if (cents == null) return '';
    const currency = (section && section.dataset && section.dataset.currency) || 'SEK';
    const negative = cents < 0;
    const abs = negative ? -cents : cents;
    const whole = Math.floor(abs / 100);
    const rem = abs % 100;
    // Thousands grouped with a comma, like the store's money format.
    let out = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (rem !== 0) out += '.' + (rem < 10 ? '0' : '') + rem;
    return (negative ? '-' : '') + out + ' ' + currency;
  }

  function updateVariantUI(section, variant) {
    if (!variant) return;

    // Price block
    const cur = section.querySelector('[data-nv-pdp-price-current]');
    const cmp = section.querySelector('[data-nv-pdp-price-compare]');
    const sav = section.querySelector('[data-nv-pdp-price-save]');
    const priceWrap = section.querySelector('[data-nv-pdp-price]');
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (cur) {
      cur.setAttribute('data-frame-cents', variant.price);
      cur.innerHTML = fmt(section, variant.price);
    }
    if (cmp) cmp.innerHTML = onSale ? fmt(section, variant.compare_at_price) : '';
    if (sav) {
      if (onSale) {
        var pct = Math.floor((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        sav.innerHTML = '-' + pct + '%';
      } else {
        sav.innerHTML = '';
      }
    }
    if (priceWrap) priceWrap.classList.toggle('nv-pdp-info__price--sale', !!onSale);
    if (cmp) cmp.style.display = onSale ? '' : 'none';
    if (sav) sav.style.display = onSale ? '' : 'none';

    // Stock dot + status
    const dot   = section.querySelector('.nv-pdp-stock__dot');
    const stat  = section.querySelector('[data-nv-pdp-stock-status]');
    const stock = section.querySelector('[data-nv-pdp-stock]');
    if (dot)   dot.classList.toggle('is-in-stock', !!variant.available);
    if (stat)  stat.textContent = variant.available ? 'In stock' : 'Out of stock';
    if (stock) stock.dataset.available = String(!!variant.available);

    // Prescription view: the headline shows frame + lens. Re-render now that
    // the frame price may have changed.
    if (window.nvRxRenderPrices) window.nvRxRenderPrices();

    // ATC button + price
    const atcBtn   = section.querySelector('.nv-pdp-atc__btn');
    const atcLabel = section.querySelector('.nv-pdp-atc__label');
    const atcPrice = section.querySelector('[data-nv-atc-price]');
    if (atcBtn)   atcBtn.disabled = !variant.available;
    if (atcPrice) atcPrice.innerHTML = fmt(section, variant.price);

    // While the Sun / Prescription toggle is in the prescription state,
    // nv-pdp-rx-toggle.js owns the button label and the price visibility —
    // the button leads to the configurator, not straight to the cart.
    // Without this guard the label snapped back to "Add to cart" whenever
    // the size changed.
    // (2026-10-15: the switch's buttons are gone; nv-pdp-rx-toggle.js marks
    // the prescription view with data-rx-active, and writes the price line.)
    const rxActive = document.querySelector('[data-nv-rx-toggle][data-rx-active="1"]');
    if (atcLabel && !rxActive) {
      atcLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
    if (window.nvRxRenderPrices) window.nvRxRenderPrices();

    // Drive the hidden product <select> so a) the form submits the right
    // variant, b) any other theme listeners (e.g. inventory blocks rendered
    // by Shopify's standard machinery) see the change. We bypass our own
    // change listener with a flag to avoid loops.
    const productSelect = section.querySelector('[data-product-select]');
    if (productSelect && productSelect.value !== String(variant.id)) {
      productSelect.value = String(variant.id);
      productSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function syncFromSize(section, sizeInput) {
    const variants = variantsFor(section);
    const selected = readSelectedOptions(section);
    const variant = findVariant(variants, selected);

    // Update visible size eyebrow value
    const sizeCurrent = section.querySelector('[data-nv-pdp-size-current]');
    if (sizeCurrent) sizeCurrent.textContent = sizeInput.value;

    // Update size hint blurb
    const blurb = sizeInput.dataset.blurb || '';
    const hint = section.querySelector('[data-nv-pdp-size-hint]');
    if (hint) hint.textContent = blurb;

    updateVariantUI(section, variant);
  }

  function bindSize(section) {
    const inputs = section.querySelectorAll('[data-nv-pdp-size-input]');
    if (!inputs.length) return;
    inputs.forEach((inp) => {
      inp.addEventListener('change', () => {
        if (inp.checked) syncFromSize(section, inp);
      });
    });
    // Initialize hint with the currently checked input.
    const checked = section.querySelector('[data-nv-pdp-size-input]:checked');
    if (checked) {
      const hint = section.querySelector('[data-nv-pdp-size-hint]');
      if (hint) hint.textContent = checked.dataset.blurb || '';
    }
  }

  function syncFromStrength(section, sel) {
    const cur = section.querySelector('[data-nv-pdp-strength-current]');
    if (cur) cur.textContent = sel.value;
    const variants = variantsFor(section);
    const selected = readSelectedOptions(section);
    const variant = findVariant(variants, selected);
    updateVariantUI(section, variant);
  }

  // Persist the user's last-selected strength across color swaps. When they
  // navigate from one reading-glasses colorway to another, we restore the
  // same strength on the new page if (a) the new product has that strength
  // and (b) it's in stock.
  const NV_STRENGTH_KEY = 'nvPdpLastStrength';

  function bindStrength(section) {
    const sel = section.querySelector('[data-nv-pdp-strength-input]');
    if (!sel) return;

    // 1. Restore from storage if the value exists in this product's options
    //    and isn't disabled (out of stock).
    try {
      const stored = window.localStorage.getItem(NV_STRENGTH_KEY);
      if (stored && stored !== sel.value) {
        const opt = sel.querySelector(
          'option[value="' + (window.CSS && CSS.escape ? CSS.escape(stored) : stored) + '"]'
        );
        if (opt && !opt.disabled) {
          sel.value = stored;
          syncFromStrength(section, sel);
        }
      }
    } catch (_) { /* private mode / blocked storage — silently skip */ }

    // 2. Save on every change.
    sel.addEventListener('change', () => {
      try { window.localStorage.setItem(NV_STRENGTH_KEY, sel.value); } catch (_) {}
      syncFromStrength(section, sel);
    });
  }

  // Plus/minus sections slide like the theme's own collapsibles (mobile
  // menu): height 0.35s, content drifting up 40px over 0.5s and fading in;
  // closing reverses over 0.3s. Same easing as theme.css .collapsible-content.
  // New design only (html.nvd); the old look keeps the instant <details>.
  const ACC_EASE = 'cubic-bezier(.25,.46,.45,.94)';

  function accMotionOn() {
    return document.documentElement.classList.contains('nvd') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function accAnimate(acc, opening) {
    const body = acc.querySelector('.nv-pdp-acc__body');
    if (!body) { acc.open = opening; return; }

    // A click mid-animation reverses from wherever the section is now
    const fromHeight = body.getBoundingClientRect().height;
    const fromPad = getComputedStyle(body).paddingBottom;
    const fromOpacity = getComputedStyle(body).opacity;
    (acc._nvAnims || []).forEach((a) => a.cancel());

    acc.open = true;
    acc.classList.toggle('is-closing', !opening);
    const full = body.getBoundingClientRect().height;
    const pad = getComputedStyle(body).paddingBottom;
    const start = acc._nvAnims ? fromHeight : (opening ? 0 : full);
    const startPad = acc._nvAnims ? fromPad : (opening ? '0px' : pad);
    const startOpacity = acc._nvAnims ? fromOpacity : (opening ? 0 : 1);

    acc.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    const size = body.animate(
      [{ height: start + 'px', paddingBottom: startPad },
       { height: (opening ? full : 0) + 'px', paddingBottom: opening ? pad : '0px' }],
      { duration: opening ? 350 : 300, easing: ACC_EASE }
    );
    const fade = body.animate(
      [{ opacity: startOpacity }, { opacity: opening ? 1 : 0 }],
      { duration: opening ? 1000 : 300, easing: ACC_EASE, fill: 'forwards' }
    );
    const drift = body.animate(
      [{ transform: opening ? 'translateY(40px)' : 'none' },
       { transform: opening ? 'none' : 'translateY(40px)' }],
      { duration: opening ? 500 : 300, easing: ACC_EASE }
    );
    acc._nvAnims = [size, fade, drift];

    size.onfinish = () => {
      if (acc._nvAnims && acc._nvAnims[0] !== size) return;
      acc._nvAnims = null;
      if (!opening) acc.open = false;
      acc.classList.remove('is-closing');
      acc.style.overflow = '';
      body.style.overflow = '';
      if (opening) {
        // Let the fade run on, then hand the element back to the stylesheet
        fade.onfinish = () => fade.cancel();
      } else {
        fade.cancel();
      }
    };
  }

  function accOpen(acc) {
    if (acc.open && !acc.classList.contains('is-closing')) return;
    if (accMotionOn()) accAnimate(acc, true);
    else acc.open = true;
  }

  function bindAccordions(section) {
    section.querySelectorAll('.nv-pdp-acc').forEach((acc) => {
      const head = acc.querySelector('.nv-pdp-acc__head');
      if (!head) return;
      head.addEventListener('click', (e) => {
        if (!accMotionOn()) return;
        e.preventDefault();
        const closing = acc.open && !acc.classList.contains('is-closing');
        accAnimate(acc, !closing);
      });
    });
  }

  function bindSeeSizing(section) {
    const btn = section.querySelector('[data-nv-pdp-see-sizing]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      // Open the Sizing accordion (first <details> in the wrap) and scroll
      // it to the top of the viewport — that's where the actual measurement
      // table lives, which is what "See sizing" promises to show.
      const acc = section.querySelector('.nv-pdp-acc-wrap .nv-pdp-acc');
      if (!acc) return;
      accOpen(acc);
      // After open, give the layout one frame to settle then scroll.
      requestAnimationFrame(() => {
        const top = acc.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  // Add to cart opens the cart drawer (2026-10-15), like an add from the
  // prescription flow, instead of leaving for the cart page — so every add
  // shows the drawer, its bundle offer and Continue shopping. Sends the same
  // ajaxProduct:added event the drawer already listens for.
  // Skipped when the click was already handled: in a prescription view
  // nv-pdp-rx-toggle.js sends the submit to the configurator (its capture
  // listener calls preventDefault first), so nothing is ever added twice.
  // Cart set to "page" in theme settings, or no drawer: normal submit.
  function bindAjaxAdd(section) {
    var form = section.querySelector('.nv-pdp-atc__form');
    if (!form || form.dataset.nvAjax === '1') return;
    form.dataset.nvAjax = '1';
    var btn = form.querySelector('.nv-pdp-atc__btn');

    function showError(msg) {
      var el = section.querySelector('[data-nv-atc-error]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'nv-pdp-atc__error';
        el.setAttribute('data-nv-atc-error', '');
        el.setAttribute('role', 'alert');
        form.insertAdjacentElement('afterend', el);
      }
      el.textContent = msg || 'We could not add this to your cart. Please try again.';
    }
    function clearError() {
      var el = section.querySelector('[data-nv-atc-error]');
      if (el) el.remove();
    }

    form.addEventListener('submit', function (e) {
      if (e.defaultPrevented) return;
      var cartType = (window.theme && theme.settings && theme.settings.cartType) || 'drawer';
      if (cartType === 'page' || !document.getElementById('CartDrawer')) return;
      e.preventDefault();
      if (form.dataset.nvAdding === '1') return;
      form.dataset.nvAdding = '1';
      if (btn) btn.classList.add('btn--loading');
      var route = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
      fetch(route, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && (data.status === 422 || data.status === 'bad_request')) {
            showError(data.description || data.message);
            return;
          }
          clearError();
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data, addToCartBtn: btn },
            bubbles: true
          }));
        })
        .catch(function () {
          // Network trouble: fall back to the ordinary submit (cart page).
          form.submit();
        })
        .then(function () {
          form.dataset.nvAdding = '';
          if (btn) btn.classList.remove('btn--loading');
        });
    });
  }

  function init(section) {
    if (!section || section.dataset.nvBoundInfo === '1') return;
    section.dataset.nvBoundInfo = '1';
    bindAjaxAdd(section);
    bindSize(section);
    bindStrength(section);
    bindSeeSizing(section);
    bindAccordions(section);
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="nv-pdp"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
